import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import type { OAuthProvider } from "@lorevok/domain";
import { AsyncButton } from "../components/AsyncButton.js";
import { OAuthButtons } from "../components/OAuthButtons.js";
import { PasswordField } from "../components/PasswordField.js";
import { UsernameField } from "../components/UsernameField.js";
import { useUsernameAvailability, usernameStatusMessageKey } from "../lib/useUsernameAvailability.js";
import {
  resendSignupConfirmation,
  sendPasswordResetEmail,
  signInWithOAuthProvider,
  signInWithPassword,
  signUpWithPassword,
  updatePassword
} from "../lib/auth.js";
import {
  clearOAuthCallbackError,
  forgetOAuthStart,
  isOAuthCallbackError,
  rememberOAuthStart
} from "../lib/oauthReturn.js";
import { requestPasswordReset } from "../apiClient.js";
import { track } from "../lib/analytics.js";
import { discordInviteUrl } from "../lib/communityLinks.js";
import { useT } from "../lib/i18n/store.js";
import { apiErrorMessage } from "../lib/apiErrorMessage.js";
import { withInlineNodes } from "../lib/inlineNodes.js";
import type { Page } from "../types.js";

/** Minimum password length for new accounts and password resets. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * How long "Resend email" stays locked after an auth email goes out. Supabase
 * refuses a repeat email to the same address for about a minute, so an earlier
 * click would only earn a rate-limit error.
 */
export const RESEND_COOLDOWN_MS = 60_000;

/**
 * Shown in place of the sign-in screen until the API says whether sign-up is
 * open. The screen's copy differs by mode (invite-only beta vs open sign-up), so
 * rendering either one early flashed the wrong text for a moment.
 */
export function AuthPageSkeleton() {
  const t = useT();
  return (
    <section className="auth-page" aria-busy="true">
      <p className="sr-only" role="status">{t("detail.loading")}</p>
      <div className="auth-card auth-card--skeleton" aria-hidden="true">
        <div className="skeleton auth-skeleton-tabs" />
        <div className="skeleton skeleton-line skeleton-line--title" />
        <div className="skeleton skeleton-line" />
        <div className="skeleton auth-skeleton-field" />
        <div className="skeleton auth-skeleton-field" />
        <div className="skeleton auth-skeleton-button" />
      </div>
    </section>
  );
}

type AuthMode = "signin" | "signup" | "reset";
type TabMode = Exclude<AuthMode, "reset">;
/** What a reader tried before being sent here (an account-only action on a novel). */
export type ReaderPrompt = "like" | "comment";

/**
 * Sign-in, and — in open-signup mode (public launch) — self-serve account
 * creation and emailed password reset. In the invite-only beta (openSignup
 * false) there is no sign-up form: the owner provisions accounts for
 * allowlisted emails and resets passwords by hand.
 *
 * With open sign-up, both tabs also offer "Continue with Google / Discord" for
 * the providers the API lists. On the sign-up tab they wait for the same 18+
 * and Terms/Privacy boxes; on the sign-in tab they don't — a brand-new account
 * made there ticks them on "Finish signing up" before it can do anything.
 */
export function AuthPage({
  onAuthenticated,
  onNavigate,
  openSignup = false,
  initialMode = "signin",
  readerPrompt,
  onSignUpPending,
  oauthProviders = [],
  onOAuthStart,
  resendCooldownMs = RESEND_COOLDOWN_MS
}: {
  onAuthenticated: () => void;
  /** Opens a legal document (Terms / Privacy) linked from the forms. */
  onNavigate?: (page: Page) => void;
  /** Public launch: show the sign-up form and the self-serve password reset. */
  openSignup?: boolean;
  initialMode?: TabMode;
  /** A reader sent here from a novel (Like, comment): the forms speak to readers. */
  readerPrompt?: ReaderPrompt;
  /** A sign-up now waits for its confirmation email (the account is not signed in yet). */
  onSignUpPending?: () => void;
  /** "Continue with …" buttons to show (open sign-up only). */
  oauthProviders?: readonly OAuthProvider[];
  /** The page is about to leave for Google / Discord (the sign-in comes back as a new page load). */
  onOAuthStart?: () => void;
  /** Lock on "Resend email" after each send (tests pass 0). */
  resendCooldownMs?: number;
}) {
  const [email, setEmail] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const usernameCheck = useUsernameAvailability(usernameInput);
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  // Sign-up needs two separate, explicit consents (Terms §3): the age, and the documents.
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [docsAccepted, setDocsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<AuthMode>(openSignup ? initialMode : "signin");
  const [resetSent, setResetSent] = useState(false);
  const [confirmEmailFor, setConfirmEmailFor] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  // The provider whose sign-in page is loading.
  const [oauthPending, setOAuthPending] = useState<OAuthProvider | null>(null);
  const cooldown = useCooldown(resendCooldownMs);
  const idBase = useId();
  const t = useT();
  const showOAuth = openSignup && oauthProviders.length > 0;

  // Back from Google / Discord without a session (cancelled there, or refused):
  // say so once, and take the error out of the address.
  useEffect(() => {
    if (!isOAuthCallbackError()) return;
    setError(t("auth.oauthFailed"));
    clearOAuthCallbackError();
    // Once, on arrival: the address is read before anything else can change it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Back from the provider's page with the browser's Back button: the page is
  // restored as it was left (back/forward cache), buttons waiting. Wake them.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      forgetOAuthStart();
      setOAuthPending(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  // sign_up_started: the first change to the sign-up form, once per visit.
  const signUpStartedRef = useRef(false);
  function noteSignUpStarted() {
    if (signUpStartedRef.current) return;
    signUpStartedRef.current = true;
    track("sign_up_started", { prompt: readerPrompt ?? null });
  }

  // Optional public Discord invite (VITE_DISCORD_URL, read through
  // discordInviteUrl like every other surface). The "request access" link only
  // appears when it is set to an https:// link.
  const discordUrl = discordInviteUrl() ?? undefined;

  function docLink(page: "terms" | "privacy", label: string): ReactNode {
    return onNavigate ? (
      <button type="button" className="auth-inline-link" onClick={() => onNavigate(page)}>
        {label}
      </button>
    ) : (
      <a className="auth-inline-link" href={`/${page}`}>
        {label}
      </a>
    );
  }

  function switchMode(next: AuthMode): void {
    setMode(next);
    setError(null);
    setResetSent(false);
    setResent(false);
  }

  async function run(action: () => Promise<void>, fallbackKey: "auth.failed" | "auth.couldNotSend") {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(apiErrorMessage(err, t, t(fallbackKey)));
    } finally {
      setBusy(false);
    }
  }

  /** Leaves for the provider's sign-in page; only a failure to get there comes back here. */
  function handleOAuth(provider: OAuthProvider) {
    setError(null);
    setOAuthPending(provider);
    onOAuthStart?.();
    rememberOAuthStart(provider);
    signInWithOAuthProvider(provider).catch((err: unknown) => {
      forgetOAuthStart();
      setOAuthPending(null);
      setError(err instanceof Error ? err.message : t("auth.oauthFailed"));
    });
  }

  /** The "or Continue with …" block under a form; on sign-up it waits for both boxes. */
  function oauthBlock(needsConsent: boolean): ReactNode {
    if (!showOAuth) return null;
    return (
      <div className="auth-oauth">
        <p className="auth-divider">
          <span>{t("auth.or")}</span>
        </p>
        <OAuthButtons
          providers={oauthProviders}
          disabled={needsConsent}
          pending={oauthPending}
          describedBy={needsConsent ? `${idBase}-oauth-hint` : undefined}
          onSelect={handleOAuth}
        />
        {needsConsent && (
          <p id={`${idBase}-oauth-hint`} className="auth-hint auth-oauth-hint">
            {t("auth.oauthNeedsConsent")}
          </p>
        )}
      </div>
    );
  }

  function handleSignIn(event: FormEvent) {
    event.preventDefault();
    // Signing in this way: an abandoned Google / Discord attempt is not this sign-in.
    forgetOAuthStart();
    void run(async () => {
      await signInWithPassword(email, password);
      // The field takes an email or a username; which one, never the value.
      track("login_completed", { method: email.trim().includes("@") ? "email" : "username" });
      onAuthenticated();
    }, "auth.failed");
  }

  function handleSignUp(event: FormEvent) {
    event.preventDefault();
    if (password !== passwordRepeat) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    if (!ageConfirmed) {
      setError(t("auth.ageRequired"));
      return;
    }
    if (!docsAccepted) {
      setError(t("auth.docsRequired"));
      return;
    }
    forgetOAuthStart();
    void run(async () => {
      // Re-check right before creating the account, so a name taken while the
      // form sat open (or still being checked) is reported here, not by Supabase.
      // A check that could not run ("error") does not block: the database
      // trigger has the final word on a taken name, reported below.
      const settled = await usernameCheck.confirm();
      const problem = settled === "error" ? null : usernameStatusMessageKey(settled);
      if (problem) throw new Error(t(problem));
      let result: Awaited<ReturnType<typeof signUpWithPassword>>;
      try {
        result = await signUpWithPassword(email, password, usernameCheck.username);
      } catch (err) {
        // The trigger refuses a name claimed in the last moment; Supabase only
        // says "Database error saving new user", so ask again to explain it.
        if ((await usernameCheck.confirm()) === "taken") throw new Error(t("username.taken"));
        throw err;
      }
      const { needsConfirmation, existingAccount } = result;
      // An email that already belongs to a confirmed account is not a new
      // sign-up (a repeat for an unconfirmed one can't be told apart and is
      // counted). The screen below stays the same for it, so the form never
      // tells who is registered.
      if (!existingAccount) track("sign_up_completed", { method: "email", needs_confirmation: needsConfirmation });
      if (needsConfirmation) {
        onSignUpPending?.();
        setConfirmEmailFor(email);
        cooldown.start();
      } else onAuthenticated();
    }, "auth.failed");
  }

  function handleReset(event: FormEvent) {
    event.preventDefault();
    // The next session comes from the emailed link, not from an abandoned Google / Discord start.
    forgetOAuthStart();
    void run(async () => {
      // Open signup: Supabase emails a reset link. Invite-only beta: the request
      // is filed for the owner, who resets the password by hand.
      if (openSignup) await sendPasswordResetEmail(email);
      else await requestPasswordReset(email);
      setResetSent(true);
      cooldown.start();
    }, "auth.couldNotSend");
  }

  /** Sends the confirmation / reset email again (open signup only). */
  function handleResend() {
    setResent(false);
    forgetOAuthStart();
    void run(async () => {
      if (confirmEmailFor) await resendSignupConfirmation(confirmEmailFor);
      else await sendPasswordResetEmail(email);
      setResent(true);
      cooldown.start();
    }, "auth.couldNotSend");
  }

  /** What to do next after an email went out: back to sign in, resend, or fix the address. */
  function emailSentActions(onBack: () => void, onChangeEmail: () => void): ReactNode {
    const wait = cooldown.secondsLeft;
    return (
      <>
        {error && <p className="auth-error" role="alert">{error}</p>}
        {resent && !error && <p className="auth-notice-resent" role="status">{t("auth.resentNote")}</p>}
        <button type="button" className="primary-button" onClick={onBack}>
          {t("auth.backToSignIn")}
        </button>
        <AsyncButton
          className="secondary"
          busy={busy}
          loadingLabel={t("auth.sending")}
          disabled={wait > 0}
          onClick={handleResend}
        >
          {wait > 0
            ? t("auth.resendIn", { time: `${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, "0")}` })
            : t("auth.resend")}
        </AsyncButton>
        <button
          type="button"
          className="auth-switch"
          onClick={() => {
            onChangeEmail();
            setResent(false);
            setError(null);
          }}
        >
          {t("auth.changeEmail")}
        </button>
      </>
    );
  }

  if (confirmEmailFor) {
    return (
      <AuthNotice
        title={t("auth.checkEmailTitle")}
        lead={t("auth.checkEmailInfo")}
        email={confirmEmailFor}
        hint={t("auth.checkSpamHint")}
      >
        {emailSentActions(
          () => {
            setConfirmEmailFor(null);
            switchMode("signin");
          },
          // Back to the sign-up form, the address still filled in.
          () => setConfirmEmailFor(null)
        )}
      </AuthNotice>
    );
  }

  if (mode === "reset" && resetSent) {
    return openSignup ? (
      <AuthNotice
        title={t("auth.resetSentTitleOpen")}
        lead={t("auth.resetSentOpen")}
        email={email}
        hint={t("auth.checkSpamHint")}
      >
        {emailSentActions(
          () => switchMode("signin"),
          () => setResetSent(false)
        )}
      </AuthNotice>
    ) : (
      // Invite-only beta: nothing was emailed — the request waits for the owner.
      <AuthNotice tone="done" title={t("auth.requestSentTitle")} lead={t("auth.requestSentInfo")} email={email}>
        <button type="button" className="primary-button" onClick={() => switchMode("signin")}>
          {t("auth.backToSignIn")}
        </button>
      </AuthNotice>
    );
  }

  if (mode === "reset") {
    return (
      <section className="auth-page">
        <div className="auth-card">
          <h1>{t("auth.resetTitle")}</h1>
          <p className="auth-subtitle">{t(openSignup ? "auth.resetSubtitleOpen" : "auth.resetSubtitle")}</p>
          <form className="auth-form" onSubmit={handleReset}>
            <label>
              {t("auth.email")}
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            {error && <p className="auth-error" role="alert">{error}</p>}
            <AsyncButton
              type="submit"
              className="primary-button"
              busy={busy}
              loadingLabel={t("auth.sending")}
            >
              {t("auth.requestReset")}
            </AsyncButton>
          </form>
          <button type="button" className="auth-switch" onClick={() => switchMode("signin")}>
            {t("auth.backToSignIn")}
          </button>
        </div>
      </section>
    );
  }

  const signUpForm = (
    <>
      <h1>{t("auth.signUpTitle")}</h1>
      <p className="auth-subtitle">{t(readerPrompt ? "auth.readerSignUpSubtitle" : "auth.signUpSubtitle")}</p>
      {/* A change event from any field bubbles here: the first one is sign_up_started. */}
      <form className="auth-form" onSubmit={handleSignUp} onChange={noteSignUpStarted}>
        <UsernameField
          id={`${idBase}-signup-username`}
          value={usernameInput}
          onChange={(value) => {
            setUsernameInput(value);
            setError(null);
          }}
          status={usernameCheck.status}
        />
        <label>
          {t("auth.email")}
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <PasswordField
          id="signup-password"
          label={t("auth.password")}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          hint={t("auth.passwordHint", { count: MIN_PASSWORD_LENGTH })}
        />
        <PasswordField
          id="signup-password-repeat"
          label={t("auth.passwordConfirm")}
          value={passwordRepeat}
          onChange={setPasswordRepeat}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
        />
        <div className="auth-checks">
          <label className="auth-check">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(event) => setAgeConfirmed(event.target.checked)}
            />
            <span>{t("auth.ageConfirm")}</span>
          </label>
          <label className="auth-check">
            <input
              type="checkbox"
              checked={docsAccepted}
              onChange={(event) => setDocsAccepted(event.target.checked)}
            />
            <span>
              {withInlineNodes(t("auth.acceptDocs"), {
                terms: docLink("terms", t("auth.termsLink")),
                privacy: docLink("privacy", t("auth.privacyLink"))
              })}
            </span>
          </label>
        </div>

        {error && <p className="auth-error" role="alert">{error}</p>}

        <AsyncButton type="submit" className="primary-button" busy={busy} loadingLabel={t("auth.working")}>
          {t("auth.signUp")}
        </AsyncButton>
      </form>
      {oauthBlock(!(ageConfirmed && docsAccepted))}
    </>
  );

  const signInForm = (
    <>
      <h1>{openSignup ? t("auth.signInTitleOpen") : t("auth.signInTitle")}</h1>
      <p className="auth-subtitle">
        {openSignup
          ? t(readerPrompt ? "auth.readerSignInSubtitle" : "auth.signInSubtitleOpen")
          : withInlineNodes(t("auth.signInSubtitle"), {
              beta: onNavigate ? (
                <button type="button" className="auth-inline-link" onClick={() => onNavigate("terms")}>
                  {t("auth.betaLinkLabel")}
                </button>
              ) : (
                t("auth.betaLinkLabel")
              )
            })}
      </p>

      {/* Invite-only beta: lead logged-out visitors to the Discord onboarding
          ("get access") as the primary action; the sign-in form below is for
          people who already have an account. */}
      {!openSignup && discordUrl && (
        <a
          className="auth-discord-cta"
          href={discordUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t("auth.requestAccessLabel")}
        </a>
      )}

      <form className="auth-form" onSubmit={handleSignIn}>
        <label>
          {t("auth.emailOrUsername")}
          <input
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <PasswordField
          id="signin-password"
          label={t("auth.password")}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          minLength={6}
        />

        {error && <p className="auth-error" role="alert">{error}</p>}

        <AsyncButton
          type="submit"
          className="primary-button"
          busy={busy}
          loadingLabel={t("auth.working")}
        >
          {t("auth.signIn")}
        </AsyncButton>
      </form>
      {oauthBlock(false)}

      <button type="button" className="auth-switch" onClick={() => switchMode("reset")}>
        {t("auth.forgot")}
      </button>

      {!openSignup && (
        <div className="auth-footer">
          <p className="auth-note">
            {withInlineNodes(t("auth.needAccess"), {
              request: discordUrl ? (
                <a className="auth-inline-link" href={discordUrl} target="_blank" rel="noreferrer noopener">
                  {t("auth.requestAccessLabel")}
                </a>
              ) : (
                t("auth.requestAccessLabel")
              )
            })}
          </p>
        </div>
      )}
    </>
  );

  // Sign in and sign up share one card so the switcher on top keeps its place
  // (and keyboard focus) when the form below it changes.
  return (
    <section className="auth-page">
      <div className="auth-card">
        {openSignup && <AuthTabs idBase={idBase} mode={mode} onSelect={switchMode} />}
        <div
          id={`${idBase}-panel`}
          role={openSignup ? "tabpanel" : undefined}
          aria-labelledby={openSignup ? `${idBase}-tab-${mode}` : undefined}
        >
          {mode === "signup" ? signUpForm : signInForm}
        </div>
      </div>
    </section>
  );
}

/**
 * Shown when the user arrives from an emailed password-reset link (Supabase's
 * PASSWORD_RECOVERY session): pick a new password, then continue signed in.
 */
export function NewPasswordPage({ onDone }: { onDone: () => void }) {
  const t = useT();
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== passwordRepeat) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-page">
      <div className="auth-card">
        <h1>{t("auth.newPasswordTitle")}</h1>
        <p className="auth-subtitle">{t("auth.newPasswordSubtitle")}</p>
        <form className="auth-form" onSubmit={handleSubmit}>
          <PasswordField
            id="new-password"
            label={t("auth.password")}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            hint={t("auth.passwordHint", { count: MIN_PASSWORD_LENGTH })}
          />
          <PasswordField
            id="new-password-repeat"
            label={t("auth.passwordConfirm")}
            value={passwordRepeat}
            onChange={setPasswordRepeat}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
          />
          {error && <p className="auth-error" role="alert">{error}</p>}
          <AsyncButton type="submit" className="primary-button" busy={busy} loadingLabel={t("auth.working")}>
            {t("auth.savePassword")}
          </AsyncButton>
        </form>
      </div>
    </section>
  );
}

/**
 * "Sign in / Sign up" switch at the top of the auth card — the same segmented
 * pill as "Catalog / My Novels". A real tablist: arrow keys, Home and End move
 * between the two tabs, and only the selected one sits in the Tab order.
 */
function AuthTabs({
  idBase,
  mode,
  onSelect
}: {
  idBase: string;
  mode: TabMode;
  onSelect: (next: TabMode) => void;
}) {
  const t = useT();
  const refs = useRef<Record<TabMode, HTMLButtonElement | null>>({ signin: null, signup: null });
  const tabs: Array<{ key: TabMode; label: string }> = [
    { key: "signin", label: t("auth.signInTitle") },
    { key: "signup", label: t("auth.tabSignUp") }
  ];

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    let next: TabMode | null = null;
    if (event.key === "Home") next = "signin";
    else if (event.key === "End") next = "signup";
    else if (event.key === "ArrowLeft" || event.key === "ArrowRight") next = mode === "signin" ? "signup" : "signin";
    if (!next) return;
    event.preventDefault();
    if (next !== mode) onSelect(next);
    refs.current[next]?.focus();
  }

  return (
    <div className="library-tabs auth-tabs" role="tablist" aria-label={t("auth.tabsAria")} onKeyDown={handleKeyDown}>
      {tabs.map((tab) => {
        const selected = mode === tab.key;
        return (
          <button
            key={tab.key}
            ref={(node) => {
              refs.current[tab.key] = node;
            }}
            id={`${idBase}-tab-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idBase}-panel`}
            tabIndex={selected ? 0 : -1}
            className={selected ? "library-tab is-active" : "library-tab"}
            onClick={() => {
              if (!selected) onSelect(tab.key);
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The screen after an auth email (or, in the invite-only beta, a reset request)
 * goes out: an icon, what happened, the address it went to, and what to do next.
 */
function AuthNotice({
  tone = "mail",
  title,
  lead,
  email,
  hint,
  children
}: {
  /** "mail": an email is on its way. "done": a request was filed, nothing to wait for in the inbox. */
  tone?: "mail" | "done";
  title: string;
  lead: string;
  email: string;
  hint?: string;
  children: ReactNode;
}) {
  const t = useT();
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The submitted form is gone; land keyboard and screen-reader users on the
  // result instead of the top of the page.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section className="auth-page">
      <div className="auth-card auth-notice">
        <span className={tone === "done" ? "auth-notice-icon auth-notice-icon--done" : "auth-notice-icon"} aria-hidden="true">
          {tone === "done" ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5 10 17.5 19 7" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <path d="m4 7 8 6 8-6" />
            </svg>
          )}
        </span>
        <h1 ref={headingRef} tabIndex={-1}>
          {title}
        </h1>
        <p className="auth-notice-lead">{lead}</p>
        <div className="auth-notice-row">
          <span className="auth-notice-label">{t("auth.email")}</span>
          <span className="auth-notice-value">{email}</span>
        </div>
        {hint && <p className="auth-notice-hint">{hint}</p>}
        <div className="auth-notice-actions">{children}</div>
      </div>
    </section>
  );
}

/** A once-a-second countdown; `start()` (re)arms it for `ms`. */
function useCooldown(ms: number): { secondsLeft: number; start: () => void } {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until <= Date.now()) return;
    const id = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= until) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [until]);

  return {
    secondsLeft: Math.max(0, Math.ceil((until - now) / 1000)),
    start: () => {
      const current = Date.now();
      setNow(current);
      setUntil(current + ms);
    }
  };
}
